import { useState, useEffect } from 'react';
import { signInWithPopup, signOut } from 'firebase/auth';
import { initializeFirebase, getFirebaseAuth, getGoogleProvider, isFirebaseConfigured } from '../config/firebase';
import { useApiError } from './useApiError';
import { useConfig } from './useConfig';

interface GoogleAuthResult {
  email: string;
  name: string;
  photoURL?: string;
  idToken: string;
}

export const useGoogleAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const { handleApiError } = useApiError();
  const { config, loading: configLoading } = useConfig();

  // Initialize Firebase when config is loaded
  useEffect(() => {
    if (!configLoading && config) {
      initializeFirebase(config);
      setIsConfigured(isFirebaseConfigured());
    }
  }, [config, configLoading]);

  const signInWithGoogle = async (): Promise<GoogleAuthResult | null> => {
    if (!isConfigured) {
      handleApiError(new Error('Google login non configurato'));
      return null;
    }

    setIsLoading(true);
    try {
      const auth = getFirebaseAuth();
      const googleProvider = getGoogleProvider();
      
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      if (!user.email) {
        throw new Error('Email non disponibile dall\'account Google');
      }

      // Get the ID token for backend verification
      const idToken = await user.getIdToken();

      // Immediately sign out from Firebase since we only need the token
      await signOut(auth);

      setIsLoading(false); // Success case
      return {
        email: user.email,
        name: user.displayName || '',
        photoURL: user.photoURL || undefined,
        idToken: idToken
      };
    } catch (error: any) {
      setIsLoading(false); // Always reset loading state on error
      
      if (error.code === 'auth/popup-closed-by-user') {
        // User closed the popup, don't show error - this is normal behavior
        return null;
      }
      
      if (error.code === 'auth/cancelled-popup-request') {
        // Another popup was already open, don't show error
        return null;
      }
      
      handleApiError(error);
      return null;
    }
  };

  return {
    signInWithGoogle,
    isLoading,
    isConfigured
  };
};